import LogRocket from 'logrocket'

// Initialize LogRocket only in production or if explicitly enabled
// Note: LogRocket project still uses old name 'ecchiquest' - update in LogRocket dashboard if needed
const LOGROCKET_APP_ID = '3az7vs/ecchiquest'
const IS_ENABLED = import.meta.env.PROD || import.meta.env.VITE_ENABLE_LOGROCKET === 'true'

let isInitialized = false

/**
 * Initialize LogRocket
 * Call this once at app startup
 */
export function initLogger() {
  if (isInitialized || !IS_ENABLED) {
    if (!IS_ENABLED) {
      console.log('[Logger] LogRocket disabled in development. Set VITE_ENABLE_LOGROCKET=true to enable.')
    }
    return
  }

  LogRocket.init(LOGROCKET_APP_ID, {
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',
    console: {
      isEnabled: true,
      shouldAggregateConsoleErrors: true,
    },
    network: {
      isEnabled: true,
      requestSanitizer: (request) => {
        // Sanitize sensitive headers
        if (request.headers['Authorization']) {
          request.headers['Authorization'] = '[REDACTED]'
        }
        return request
      },
    },
    dom: {
      // Mask sensitive inputs
      inputSanitizer: true,
      textSanitizer: true,
    },
  })

  isInitialized = true
  console.log('[Logger] LogRocket initialized')
}

/**
 * Identify the current user
 * Call this after user signs in or on app load if user exists
 */
export function identifyUser(userId: string, traits?: {
  name?: string
  email?: string
  level?: number
  activeCharacter?: string
  contentLevel?: number
}) {
  if (!IS_ENABLED) return

  const userTraits: Record<string, string | number | boolean> = {
    name: traits?.name || 'Anonymous',
  }
  if (traits?.email) userTraits.email = traits.email
  if (traits?.level !== undefined) userTraits.level = traits.level
  if (traits?.activeCharacter) userTraits.activeCharacter = traits.activeCharacter
  if (traits?.contentLevel !== undefined) userTraits.contentLevel = traits.contentLevel

  LogRocket.identify(userId, userTraits)
}

type TrackProperties = Record<string, string | number | boolean | string[] | number[] | boolean[] | null | undefined>

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, properties?: TrackProperties) {
  if (!IS_ENABLED) {
    console.log(`[Logger] Event: ${eventName}`, properties)
    return
  }

  LogRocket.track(eventName, properties)
}

/**
 * Log an error with context
 */
export function logError(error: Error, context?: Record<string, string | number | boolean>) {
  // Always log to console
  console.error('[Logger] Error:', error, context)

  if (!IS_ENABLED) return

  LogRocket.captureException(error, {
    extra: context,
  })
}

/**
 * Log a message with optional data
 */
export function log(level: 'info' | 'warn' | 'error', message: string, data?: Record<string, unknown>) {
  const logFn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log
  logFn(`[${level.toUpperCase()}] ${message}`, data || '')

  if (!IS_ENABLED) return

  // LogRocket captures console logs automatically
}

/**
 * Get the LogRocket session URL (useful for support)
 */
export function getSessionURL(): Promise<string | null> {
  if (!IS_ENABLED) return Promise.resolve(null)

  return new Promise((resolve) => {
    LogRocket.getSessionURL((url) => {
      resolve(url)
    })
  })
}

// Pre-defined event names for consistency
export const LogEvents = {
  // Tasks
  TASK_CREATED: 'task_created',
  TASK_COMPLETED: 'task_completed',
  TASK_DELETED: 'task_deleted',

  // Habits
  HABIT_CREATED: 'habit_created',
  HABIT_COMPLETED: 'habit_completed',
  STREAK_MILESTONE: 'streak_milestone',

  // Progression
  LEVEL_UP: 'level_up',
  XP_GAINED: 'xp_gained',
  MILESTONE_REACHED: 'milestone_reached',

  // Gacha
  GACHA_PULL_SINGLE: 'gacha_pull_single',
  GACHA_PULL_MULTI: 'gacha_pull_multi',
  OUTFIT_UNLOCKED: 'outfit_unlocked',

  // Character
  CHARACTER_SWITCHED: 'character_switched',
  AFFECTION_GAINED: 'affection_gained',
  OUTFIT_EQUIPPED: 'outfit_equipped',

  // App
  APP_OPENED: 'app_opened',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  SETTINGS_CHANGED: 'settings_changed',
  DATA_EXPORTED: 'data_exported',
  DATA_IMPORTED: 'data_imported',

  // Pomodoro
  POMODORO_STARTED: 'pomodoro_started',
  POMODORO_COMPLETED: 'pomodoro_completed',
  POMODORO_CANCELLED: 'pomodoro_cancelled',

  // Projects
  PROJECT_CREATED: 'project_created',
  PROJECT_COMPLETED: 'project_completed',
  BOSS_DAMAGED: 'boss_damaged',
} as const

export type LogEvent = typeof LogEvents[keyof typeof LogEvents]

// Export LogRocket instance for advanced usage
export { LogRocket }
